import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import classnames from 'classnames';

import { BuildingsResponse } from 'apolloClient/types';

import useDebounce from '../../hooks/useDebounce';
import NoHoverPrefetchLink from '../../src/shared/NoHoverPrefetchLink';
import {
  flattenStrapiBulkDataItems,
  flattenStrapiDataItem,
} from '../../lib/flattenStrapiBulkDataItems';
import rightArrow from '../../public/images/arrow-right.svg';
import iconClose from '../../public/images/close.svg';

type BuildingsProps = {
  fetchMoreBuildings: (params: { page?: number; search?: string }) => void;
  buildings?: BuildingsResponse;
  buildingSearch: string;
  setBuildingSearch: (buildingSearch: string) => void;
};

const Buildings: React.FC<BuildingsProps> = ({
  buildings,
  fetchMoreBuildings,
  buildingSearch,
  setBuildingSearch,
}) => {
  const router = useRouter();

  const filteredBuildings = flattenStrapiBulkDataItems(buildings?.data);
  const totalBuildings = buildings?.meta?.pagination.total;
  const [listBuildingsOpen, setListBuildingsOpen] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const isFetchingRef = useRef(false);
  const lastFetchedPageRef = useRef(0);

  useEffect(() => {
    if (buildingSearch !== '') {
      setBuildingSearch('');
    }
    fetchMoreBuildings({ search: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  useDebounce({
    callback: () => {
      isFetchingRef.current = false;
      lastFetchedPageRef.current = 0;
      fetchMoreBuildings({ search: buildingSearch });
    },
    changingValue: buildingSearch,
    timeout: 500,
    callOnInitial: false,
  });

  useDebounce({
    callback: () => {
      if (!buildings?.meta?.pagination || isFetchingRef.current) return;

      const nextPage = (buildings.meta.pagination.page || 0) + 1;

      // Prevent fetching the same page twice
      if (nextPage === lastFetchedPageRef.current) return;

      if (totalBuildings && filteredBuildings.length >= totalBuildings) {
        return;
      }

      isFetchingRef.current = true;
      lastFetchedPageRef.current = nextPage;

      fetchMoreBuildings({
        page: nextPage,
        search: buildingSearch,
      });

      // Reset fetching flag after a delay
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 500);
    },
    changingValue: scrollTop,
    timeout: 800,
    callOnInitial: false,
  });

  function onBuildingsListScroll(target: HTMLDivElement) {
    if (!totalBuildings || filteredBuildings.length >= totalBuildings) return;
    if (isFetchingRef.current) return;

    if (target.clientHeight + target.scrollTop > target.scrollHeight - 300) {
      // Only update scrollTop if it changed by more than 50px to reduce re-renders
      if (Math.abs(target.scrollTop - scrollTop) > 50) {
        setScrollTop(target.scrollTop);
      }
    }
  }

  const changeInputValue = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const value = event.target.value;
    setBuildingSearch(value);
  };

  const handleClearClick = () => {
    setBuildingSearch('');
  };

  const handleDesktopLinkClick = () => {
    setListBuildingsOpen(false);
  };

  const handleMobileLinkClick = () => {
    setListBuildingsOpen(false);
  };

  return (
    <div className="relative w-full h-auto pb-3 border-b border-dashed xl:pb-0 border-gold-dark xl:w-auto xl:border-b-0 group">
      {/* Desktop trigger */}
      <div className="relative hidden pr-6 cursor-pointer  xl:flex">
        <span className="pt-1 text-black xl:pt-0 whitespace-nowrap hover:text-gold-hover">
          Buildings
        </span>
      </div>

      {/* Desktop dropdown */}
      <div className="hidden pt-3 lg:pt-[16px] top-4 xl:absolute xl:group-hover:block xl:-left-10 xl:w-[360px] z-120">
        <div className="bg-white z-120 xl:border top-4 border-gray ">
          <div className="relative">
            <div className="xl:absolute hidden xl:group-hover:block w-3 h-3 bg-white -top-1.7 left-16 border-t border-l border-gray transform rotate-45" />
            <div className="h-3 bg-white -top-1.7 left-16" />
            <div className="relative mx-4 mb-3">
              <input
                className="w-full p-[8px] pl-[16px]  mt-[2px] text-16-26-0.3 bg-white border text-black border-gray close-input "
                type="text"
                placeholder="Search Buildings"
                value={buildingSearch}
                onChange={changeInputValue}
              />
              <button
                onClick={handleClearClick}
                className="absolute md:top-[18px] md:right-[11px]"
              >
                <Image
                  src={iconClose}
                  alt="close popup"
                  width={15}
                  height={15}
                />
              </button>
            </div>
            <div
              className="p-4 overflow-y-auto overscroll-contain h-96 xl:pt-[0px] xl:h-[520px]"
              onScroll={(event) => {
                onBuildingsListScroll(event.target as HTMLDivElement);
              }}
            >
              <div className="space-y-4 text-[16px] leading-[34px]">
                {filteredBuildings?.map((building) => {
                  const neighborhoodData = flattenStrapiDataItem(
                    building.neighborhood?.data
                  );
                  return (
                    <div
                      key={`header-search-building-${building.slug}-${neighborhoodData.slug}`}
                      className="grid grid-cols-2 hover:text-gold-hover"
                    >
                      <span className="block font-sohneKraftig leading-[1.3]">
                        <NoHoverPrefetchLink
                          href={`/${neighborhoodData.slug}/${building.slug}`}
                          className="hover:text-gold-hover"
                          onClick={handleDesktopLinkClick}
                        >
                          {building.name}
                        </NoHoverPrefetchLink>
                      </span>
                      <p className="block pl-[20px] leading-[1.3]">
                        {neighborhoodData.name || ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile trigger */}
      <div
        onClick={() => setListBuildingsOpen(!listBuildingsOpen)}
        className="relative flex pr-6 cursor-pointer hover:text-gold-hover xl:hidden close-building text-18-23-0.3 mt-[-4px] !color-gold"
      >
        Buildings
        <div className="pt-0.5 pl-[13px] xl:hidden">
          <Image
            className={classnames({
              'transform rotate-90': listBuildingsOpen,
            })}
            src={rightArrow}
            alt="buildings"
            height={15}
            width={12}
          />
        </div>
      </div>

      {/* Mobile dropdown */}
      {listBuildingsOpen && (
        <div className="pt-[33px] pb-[21px] md:w-80">
          <div className="bg-white z-120 xl:border top-4 md:w-80 border-gray ">
            <div className="relative">
              <div className="xl:absolute hidden xl:group-hover:block w-3 h-3 bg-white -top-1.7 left-16 border-t border-l border-gray transform rotate-45" />
              <div className="relative p-2 pl-[1px] overflow-scroll z-120">
                <input
                  className="relative w-full p-[8px] pl-[16px]  text-16-26-0.3 bg-white border z-120 border-gray"
                  type="text"
                  placeholder="Search Buildings"
                  value={buildingSearch}
                  onChange={changeInputValue}
                />
                <button
                  onClick={handleClearClick}
                  className="absolute top-[16px] right-[22px] md:top-[16px] md:right-[20px] z-[999]"
                >
                  <Image
                    src={iconClose}
                    alt="close popup"
                    width={15}
                    height={15}
                  />
                </button>
              </div>
              <div
                className="relative p-2 pl-[1px] overflow-scroll z-120 h-76"
                onScroll={(event) =>
                  onBuildingsListScroll(event.target as HTMLDivElement)
                }
              >
                <div className="space-y-4 text-[16px] leading-[34px]">
                  {filteredBuildings?.map((building) => (
                    <div
                      key={building.id}
                      className="grid grid-cols-2 gap-[30px]"
                    >
                      <div className="block mr-3 break-words whitespace-normal font-sohneKraftig leading-[1.3]">
                        <NoHoverPrefetchLink
                          className="text-17-34-0.3 xl:text-16-34-0.3 leading-[1.3]"
                          href={`/${building?.neighborhood?.data?.attributes?.slug}/${building.slug}`}
                          onClick={handleMobileLinkClick}
                        >
                          {building.name}
                        </NoHoverPrefetchLink>
                      </div>
                      <span className="block text-17-34-0.3 xl:text-16-34-0.3 leading-[1.3]">
                        {building.neighborhood?.data?.attributes?.name || ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Buildings;
